import React, { useEffect, useState } from "react";

import { useStore } from "../../../Store";
import PageLayout from "../../Controls/PageLayout";
import Section from "../../Controls/Sections";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  Image,
  Link,
  List,
  ListItem,
  Text,
} from "@chakra-ui/react";
import { BadgePill } from "../../BadgePill/BadgePill";
import { DetailsAccordion } from "../../Accordion/DetailsAccordion";
import { formatDate } from "../../../utils/formatDate";
import ConfirmationOfConsent from "./ConfirmationOfConsent";
import {
  useGetIndividualConsentData,
  IndividualConsentData,
} from "./DashboardLanding/ManagePortalHooks";
import ManagementPortalContainer from "./ManagementPortalContainer";

export default function WithdrawalSuccess() {
  const { setLoading, setShowPopup, setShowPopupError, setPopupErrorMessage } =
    useStore();

  const navigate = useNavigate();
  const { consentId } = useParams();

  const [popupOutConfirmationOfConsent, setPopUpConfirmationOfConsent] =
    useState(false);

  const [consentData, setConsentData] = useState<IndividualConsentData>();
  const GetManagePortalData = useGetIndividualConsentData(
    setConsentData,
    () => {},
    consentId
  );

  useEffect(() => {
    setLoading(false);
    if (!consentId) {
      window.location.href = "/DashboardLanding";
    }
    GetManagePortalData();
  }, []);

  const consent = consentData?.consent.consent;
  const dataHolderBrand = consentData?.consent.dataHolderBrand;

  const softwareProduct = consentData?.softwareProduct.softwareProduct;
  const softwareProductWording =
    consentData?.softwareProductWording?.softwareProductWording;

  const dataRecipient =
    consentData?.softwareProduct?.softwareProduct.dataRecipientBrand
      ?.dataRecipient;

  let startTime = formatDate(consent?.startTime);
  let endTime = formatDate(consent?.endTime);
  let withdrawnTime = formatDate(consent?.withdrawnTime ?? "");

  return (
    <ManagementPortalContainer>
      <Section isGreen={true}>
        <Alert status="success">
          <AlertIcon />
          You have successfully stopped sharing the data below with us. We will
          no longer have access to this data. This information has been
          archived.
        </Alert>
      </Section>
      <PageLayout>
        <Heading variant="pageHeading">Withdrawal Success</Heading>

        <Divider />

        <HStack spacing={6} my={4}>
          <Box>
            <Image
              maxH={"100px"}
              maxW={"100px"}
              src={dataHolderBrand?.logoUri}
              alt="Data holder Logo"
              fallbackSrc="https://via.placeholder.com/150"
            />
          </Box>
          <Box flex={1}>
            <Text mb={2} fontWeight="bold">
              {dataHolderBrand?.brandName}
            </Text>

            <Text>Product: {softwareProduct?.softwareProductName}</Text>
          </Box>

          <Box>
            <BadgePill label="Cancelled" status="secondary" />
          </Box>
        </HStack>
        <Box>
          <Text fontStyle="italic">
            We are no longer collecting and using your data for this product.
          </Text>
        </Box>

        <Box>
          <ConfirmationOfConsent
            showPopup={popupOutConfirmationOfConsent}
            handleClose={() => setPopUpConfirmationOfConsent(false)}
          />

          <Button
            my={2}
            variant="link"
            onClick={() => {
              setPopUpConfirmationOfConsent(true);
            }}
          >
            View your consent withdrawal confirmation
          </Button>

          <Divider my={3} />

          <Heading fontSize="lg">Data we've collected</Heading>

          {/* Consents */}

          {/* Individual */}
          {consent?.customerType === "Individual" ? (
            <>
              {/* Name and occupation */}
              {consent?.nameAndOccupationConsent === "RequestedAndConsented" &&
                consent?.nameOccupationContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    id="accordionNameOccupation"
                    title="Name and occupation"
                  >
                    <List>
                      <ListItem>Name</ListItem>
                      <ListItem>Occupation</ListItem>
                    </List>
                  </DetailsAccordion>
                )}

              {/* Contact details  */}
              {consent?.contactDetailsConsent === "RequestedAndConsented" &&
                consent?.nameOccupationContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    id="accordionContactDetails"
                    title="Contact details"
                  >
                    <List>
                      <ListItem>Phone</ListItem>
                      <ListItem>Email address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Residential address</ListItem>
                    </List>
                  </DetailsAccordion>
                )}

              {/* Name, occupation and contact details  */}
              {consent?.nameOccupationContactDetailsConsent ===
                "RequestedAndConsented" && (
                <DetailsAccordion
                  id="accordionNameOccupationContactDetails"
                  title="Name, occupation and contact details"
                >
                  <List>
                    <ListItem>Name</ListItem>
                    <ListItem>Occupation</ListItem>
                    <ListItem>Phone</ListItem>
                    <ListItem>Email address</ListItem>
                    <ListItem>Mail address</ListItem>
                    <ListItem>Residential address</ListItem>
                  </List>
                </DetailsAccordion>
              )}
            </>
          ) : null}

          {/* Business */}
          {consent?.customerType === "Business" ? (
            <>
              {/* Organisation profile   */}
              {consent?.organisationProfileConsent ===
                "RequestedAndConsented" &&
                consent?.organisationProfileAndContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    id="accordionOrganisationProfile"
                    title="Organisation profile"
                  >
                    <List>
                      <ListItem>Agent name and role</ListItem>
                      <ListItem>Organisation name</ListItem>
                      <ListItem>Organisation numbers (ABN or ACN)</ListItem>
                      <ListItem>Charity status</ListItem>
                      <ListItem>Establishment date</ListItem>
                      <ListItem>Industry</ListItem>
                      <ListItem>Organisation type</ListItem>
                      <ListItem>Country of registration</ListItem>
                    </List>
                  </DetailsAccordion>
                )}

              {/* Organisation contact details  */}
              {consent?.organisationContactDetailsConsent ===
                "RequestedAndConsented" &&
                consent?.organisationProfileAndContactDetailsConsent !==
                  "RequestedAndConsented" && (
                  <DetailsAccordion
                    id="accordionOrganisationContactDetails"
                    title="Organisation contact details"
                  >
                    <List>
                      <ListItem>Organisation address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Phone number</ListItem>
                    </List>
                  </DetailsAccordion>
                )}

              {/* Organisation profile and contact details  */}
              {consent?.organisationProfileAndContactDetailsConsent ===
                "RequestedAndConsented" && (
                <DetailsAccordion
                  id="accordionOrgProfileContactDetails"
                  title="Organisation profile and contact details"
                >
                  <List>
                    <ListItem>Agent name and role</ListItem>
                    <ListItem>Organisation name</ListItem>
                    <ListItem>Organisation numbers (ABN or ACN)</ListItem>
                    <ListItem>Charity status</ListItem>
                    <ListItem>Establishment date</ListItem>
                    <ListItem>Industry</ListItem>
                    <ListItem>Organisation type</ListItem>
                    <ListItem>Country of registration</ListItem>
                    <ListItem>Organisation address</ListItem>
                    <ListItem>Mail address</ListItem>
                    <ListItem>Phone number</ListItem>
                  </List>
                </DetailsAccordion>
              )}
            </>
          ) : null}

          {/* Account name, type and balance  */}
          {consent?.accountNameTypeAndBalanceConsent ===
            "RequestedAndConsented" &&
            consent?.accountBalanceAndDetailsConsent !==
              "RequestedAndConsented" && (
              <DetailsAccordion
                id="accordionAccountTypeBalance"
                title="Account name, type and balance"
              >
                <List>
                  <ListItem>Name of account</ListItem>
                  <ListItem>Type of account</ListItem>
                  <ListItem>Account balance</ListItem>
                </List>
              </DetailsAccordion>
            )}

          {/* Account numbers and features  */}
          {consent?.accountNumbersAndFeaturesConsent ===
            "RequestedAndConsented" &&
            consent?.accountBalanceAndDetailsConsent !==
              "RequestedAndConsented" && (
              <DetailsAccordion
                id="accordionAccountFeatures"
                title="Account numbers and features"
              >
                <List>
                  <ListItem>Account number</ListItem>
                  <ListItem>Interest rates</ListItem>
                  <ListItem>Fees</ListItem>
                  <ListItem>Discounts</ListItem>
                  <ListItem>Account terms</ListItem>
                  <ListItem>Account mail address</ListItem>
                </List>
              </DetailsAccordion>
            )}

          {/* Account balance and details */}
          {consent?.accountBalanceAndDetailsConsent ===
            "RequestedAndConsented" && (
            <DetailsAccordion
              id="accordionAccountBalanceDetails"
              title="Account balance and details"
            >
              <List>
                <ListItem>Name of account</ListItem>
                <ListItem>Type of account</ListItem>
                <ListItem>Account balance</ListItem>
                <ListItem>Account number</ListItem>
                <ListItem>Interest rates</ListItem>
                <ListItem>Fees</ListItem>
                <ListItem>Discounts</ListItem>
                <ListItem>Account terms</ListItem>
                <ListItem>Account mail address</ListItem>
              </List>
            </DetailsAccordion>
          )}

          {/* Transaction details */}
          {consent?.transactionDetailsConsent === "RequestedAndConsented" && (
            <DetailsAccordion
              id="accordionTransactionDetails"
              title="Transaction details"
            >
              <List>
                <ListItem>Incoming and outgoing transactions</ListItem>
                <ListItem>Amounts</ListItem>
                <ListItem>Dates</ListItem>
                <ListItem>Descriptions of transactions</ListItem>
                <ListItem>
                  Who you've sent money to and recieved from (e.g. their name)
                </ListItem>
              </List>
            </DetailsAccordion>
          )}

          {/* Direct debits and scheduled payments */}
          {consent?.directDebitsAndScheduledPaymentsConsent ===
            "RequestedAndConsented" && (
            <DetailsAccordion
              id="accordionDirectDebits"
              title="Direct debits and scheduled payments"
            >
              <List>
                <ListItem>Direct debits</ListItem>
                <ListItem>Scheduled payments</ListItem>
              </List>
            </DetailsAccordion>
          )}

          {/* Saved payees */}
          {consent?.savedPayeesConsent === "RequestedAndConsented" && (
            <DetailsAccordion id="accordionSavedPayees" title="Saved payees">
              <List>
                <ListItem>
                  Names and details of accounts you have saved (e.g. their BSB
                  and Account Number, BPay CRN and Biller code, or NPP PayID)
                </ListItem>
              </List>
            </DetailsAccordion>
          )}

          <Heading fontSize="md" mt={3} mb={2}>
            What happened to your shared data?
          </Heading>

          <Text>
            The data you have shared has been{" "}
            {softwareProductWording?.canDeIdentifyData
              ? consent?.deleteMyData
                ? "deleted"
                : "de-identified"
              : "deleted"}
          </Text>

          <Link
            my={2}
            href={dataRecipient?.consumerDataRightsPolicyUri}
            aria-label={"Link To " + dataRecipient?.consumerDataRightsPolicyUri}
            target="_blank"
            rel="noreferrer"
          >
            Learn more
          </Link>
        </Box>
      </PageLayout>

      <Section>
        <DetailsAccordion
          title="How your data was used"
          id="accordionAdditionalUses"
        >
          <Heading fontSize="sm">Purpose of data sharing</Heading>

          <Text>
            {softwareProductWording?.withdrawalImpactPurposeOfSharingYourData}
          </Text>

          <Heading fontSize="sm">Sharing with third parties</Heading>
          <Text>
            Supporting third parties no longer have access to your data. They
            were bound by the same terms of this arrangement.
          </Text>
          <Link
            href={dataRecipient?.consumerDataRightsPolicyUri}
            aria-label={"Link To " + dataRecipient?.consumerDataRightsPolicyUri}
            target="_blank"
            rel="noreferrer"
          >
            Learn more
          </Link>
        </DetailsAccordion>
        {/* Key dates */}
        <DetailsAccordion title="Key dates" id="accordionKeyDates">
          <Heading fontSize="sm">When you gave consent</Heading>
          <Text>{startTime}</Text>

          {consent?.status?.toLowerCase() !== "expired" &&
          consent?.status?.toLowerCase() !== "withdrawn" ? (
            <>
              <Heading fontSize="sm">When your consent will expire</Heading>
              <Text>{endTime}</Text>
            </>
          ) : consent?.status.toLowerCase() === "expired" ? (
            <>
              <Heading fontSize="sm">When your consent expired</Heading>
              <Text>{withdrawnTime}</Text>
            </>
          ) : (
            <>
              <Heading fontSize="sm">When your consent was withdrawn</Heading>
              <Text>{withdrawnTime}</Text>
            </>
          )}

          <Heading fontSize="sm">Sharing period</Heading>
          {/* <p>{startTime} - {withdrawnTime !== null ? withdrawnTime : endTime}</p> */}
          <Text>
            {startTime} - {endTime}
          </Text>

          <Heading fontSize="sm">How often we access your data</Heading>
          {consent?.isOneOffConsent === true ? (
            <Text>Your data was accessed once</Text>
          ) : (
            <Text>
              We do this everytime you log into{" "}
              <Text as="span">{dataRecipient?.legalEntityName}</Text>
            </Text>
          )}
        </DetailsAccordion>
      </Section>
      <Section isGrey={true}>
        <HStack spacing={6}>
          <Box flex={1}>
            <Text>
              Please read our{" "}
              <Link
                href={dataRecipient?.consumerDataRightsPolicyUri}
                aria-label={
                  "Link To " + dataRecipient?.consumerDataRightsPolicyUri
                }
                target="_blank"
                rel="noreferrer"
              >
                Consumer Data Right policy
              </Link>{" "}
              for more information on how{" "}
              <b>{dataRecipient?.legalEntityName}</b> handles your data.
            </Text>
          </Box>

          <Button
            aria-label="Close Button"
            onClick={() => navigate("/DashboardLanding")}
          >
            Close
          </Button>
        </HStack>
      </Section>
    </ManagementPortalContainer>
  );
}
